import { expVal, expValEquals } from '@confluence/feature-experiments';
import { cfetch } from '@confluence/network';
import {
	superbatchCSSRecorder,
	superbatchJSRecorder,
} from '@confluence/ssr-utilities/entry-points/SuperbatchResourceRecorder.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { stringifyForSSR } from '@confluence/ssr-utilities/entry-points/stringifyForSSR.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import {
	WebPanelLocationQueryArtifact as WebPanelLocationQuery,
	type WebPanelLocationQueryData,
	type WebPanelLocationQueryVariables,
} from '@confluence/web-panel-location/entry-points/WebPanelLocationQueryArtifacts.ts';
import { preloadWebPanelLocations } from '@confluence/web-panel-location/entry-points/preloadWebPanelLocation.ts';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';

import { convertResourceToScriptTags } from './convertResourceToScriptTags.ts';

// Track the connect resources promise so it can be awaited before rendering head
let connectWebResourcesPromise: Promise<void> | null = null;
type WebPanel = NonNullable<NonNullable<WebPanelLocationQueryData['webPanels']>[number]>;

export const getConnectWebResourcesPromise = (): Promise<void> | null => connectWebResourcesPromise;

// For testing: reset the promise state
export const __resetConnectWebResourcesPromise = (): void => {
	connectWebResourcesPromise = null;
};

const getPanelsByLocation = (webPanels?: WebPanelLocationQueryData['webPanels'] | null) => {
	const panelsByLocation = new Map<string, WebPanel[]>();

	webPanels?.forEach((panel) => {
		if (panel?.location) {
			const panels = panelsByLocation.get(panel.location) ?? [];
			panels.push(panel);
			panelsByLocation.set(panel.location, panels);
		}
	});

	return panelsByLocation;
};

export const preloadConnectWebResources = (
	contentId?: string,
	locations: string[] = [ATL_GENERAL, ATL_FOOTER],
): Promise<void> => {
	const promise = (async () => {
		if (!expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false)) {
			return;
		}

		// When early webpanel initialization is enabled, check if there are any webpanels
		// before preloading connect resources
		if (contentId) {
			const uniqueLocations = Array.from(new Set(locations.filter(Boolean)));
			if (uniqueLocations.length === 0) {
				return;
			}

			let panelsByLocation = new Map<string, WebPanel[]>();
			const webPanelPromises = expValEquals('web_panel_query_batch_call', 'isEnabled', true)
				? [
						preloadWebPanelLocations({
							contentId,
							locations: uniqueLocations,
						}).then((result) => {
							panelsByLocation = getPanelsByLocation(result.data?.webPanels);
						}),
					]
				: uniqueLocations.map((location) =>
						query<WebPanelLocationQueryData, WebPanelLocationQueryVariables>({
							query: WebPanelLocationQuery,
							variables: {
								contentId,
								location,
							},
						}).then((result) => {
							const locationPanels: WebPanel[] = [];
							result.data?.webPanels?.forEach((panel) => {
								if (panel) {
									locationPanels.push(panel);
								}
							});
							panelsByLocation.set(location, locationPanels);
						}),
					);

			// Wait for web panel probes, but don't fail fast if one errors
			const errors = await waitForAllReturnWhenError(webPanelPromises);

			if (errors) {
				// If webpanel queries failed, continue with preloading to avoid blocking
				ssrPrepareErrorsClient.submitError(
					new Error(
						`Failed to query webpanels before preloading connect resources: ${errors
							.map((e) => e.message)
							.join(', ')}`,
					),
				);
			} else {
				const hasWebPanels = uniqueLocations.some(
					(location) => (panelsByLocation.get(location)?.length ?? 0) > 0,
				);

				if (!hasWebPanels) {
					return;
				}
			}
		}

		try {
			const response = await cfetch('/wiki/rest/webResources/1.0/resources', {
				method: 'POST',
				body: JSON.stringify({
					r: [
						'confluence.web.resources:querystring',
						'confluence.web.resources:navigator-context',
						'com.atlassian.plugins.atlassian-connect-plugin:confluence-atlassian-connect-resources-v5',
						'confluence.web.resources:view-content',
						'confluence.macros.advanced:get-more',
						'com.atlassian.confluence.plugins.confluence-view-file-macro:view-file-macro-resources',
					],
					c: [],
					xc: ['_super'],
					xr: [
						'com.atlassian.plugins.jquery:jquery',
						'com.atlassian.plugins.jquery:jquery-migrate',
					],
				}),
			});

			if (!response.ok) {
				throw new Error(`Failed to preload web resources: ${response.status}`);
			}

			const data = await response.json();

			// unparsedData in the response will later be claimed by window.WRM.data.claim()
			// This need to be fore all the JS scripts because they might claim the data while executing.
			if (data?.unparsedData) {
				const entries = [] as string[];
				for (const [key, value] of Object.entries(data.unparsedData)) {
					// value is a stringified JSON object.
					entries.push(`d["${key}"]=${stringifyForSSR(value)}`);
				}
				superbatchJSRecorder.addConnectResource(
					`<script>(function(d){if(d){${entries.join(';')}}})(window?.WRM?._unparsedData)</script>`,
				);
			}

			superbatchJSRecorder.addConnectResource(
				data?.resources
					?.filter((resource) => resource.resourceType === 'JAVASCRIPT')
					?.map((resource) => convertResourceToScriptTags(resource))
					.join('\n'),
			);

			superbatchCSSRecorder.addConnectResource(
				data?.resources
					?.filter((resource) => resource.resourceType === 'CSS')
					?.map((resource) => convertResourceToScriptTags(resource))
					.join('\n'),
			);
		} catch (error) {
			ssrPrepareErrorsClient.submitError(
				new Error(`Unable to preload connect web resources: ${error.message}`),
			);
		}
	})();

	connectWebResourcesPromise = promise;
	return promise;
};
